import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Grid, 
  Group, 
  Text, 
  TextInput, 
  Select, 
  Badge, 
  ActionIcon, 
  Modal, 
  Button, 
  Paper,
  Collapse,
  Transition 
} from '@mantine/core';
import { IconCalendar, IconDots, IconChevronUp, IconChevronDown } from '@tabler/icons-react';
import { DatePickerInput } from '@mantine/dates';

// Helper for inline label + input with half-width inputs
const Field = ({ label, component: Component = TextInput, showEllipsis = false, onEllipsisClick, ...props }) => (
  <Group spacing="xs" align="center" style={{ marginBottom: 8 }}>
    <Text size="sm" weight={500} style={{ width: 100 }}>
      {label}:
    </Text>
    <Component size="xs" style={{ flex: 1 }} {...props} />
    {showEllipsis && (
      <ActionIcon size="xs" variant="subtle" onClick={onEllipsisClick}>
        <IconDots size={12} />
      </ActionIcon>
    )}
  </Group>
);

// Helper for date fields
const DateField = ({ label, ...props }) => (
  <Group spacing="xs" align="center" style={{ marginBottom: 8 }}>
    <Text size="sm" weight={500} style={{ width: 100 }}>
      {label}:
    </Text>
    <DatePickerInput size="xs" icon={<IconCalendar size={14} />} style={{ flex: 1 }} {...props} />
  </Group>
);

export default function HeaderGrid({ customer, collapsed: externalCollapsed = false, onToggleCollapse }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  
  // Use external collapsed state if provided, otherwise use internal state
  const collapsed = externalCollapsed !== undefined ? externalCollapsed : internalCollapsed;

  // Update internal state when external state changes
  useEffect(() => {
    if (externalCollapsed !== undefined) {
      setInternalCollapsed(externalCollapsed);
    }
  }, [externalCollapsed]);

  const handleEllipsisClick = (fieldName) => {
    setModalTitle(`Search ${fieldName}`);
    setModalOpen(true);
  };

  const toggleCollapsed = () => {
    if (onToggleCollapse) {
      // Use external toggle handler if provided
      onToggleCollapse();
    } else {
      // Use internal state if no external handler
      setInternalCollapsed(!internalCollapsed);
    }
  };

  // Default date values
  const defaultDate = new Date('2025-07-30');
  const pricedDate = new Date('2025-07-30');
  const quotedDate = new Date('2025-07-30');
  const validUntilDate = new Date('2025-08-29');

  return (
    <Box mt="md" p="md">
      {/* Always visible top section with collapse/expand button */}
      <Paper withBorder p="xs" mb="xs">
        <Group position="apart" align="center">
          <Group spacing="xl">
            <Field label="Quote No." defaultValue="810408" readOnly />
            <Field label="Quote Type" component={Select} data={["Quotation", "Estimate", "Proposal"]} defaultValue="Quotation" />
          </Group>
          <ActionIcon 
            variant="subtle" 
            onClick={toggleCollapsed}
            size="md"
            title={collapsed ? "Expand Header" : "Collapse Header"}
          >
            {collapsed ? <IconChevronDown size={16} /> : <IconChevronUp size={16} />}
          </ActionIcon>
        </Group>
      </Paper>

      {/* Collapsible section */}
      <Collapse in={!collapsed} transitionDuration={400} transitionTimingFunction="ease-in-out">
        <Grid gutter="xs">
          <Grid.Col span={4}>
            <Paper withBorder p="xs">
              <Field label="Our Ref" placeholder="—" />
              <Field label="Your Ref" placeholder="—" />
              <Field label="B SO Order" placeholder="—" />
              <Field label="B Cust PO" placeholder="—" />
            </Paper>
          </Grid.Col>

          <Grid.Col span={4}>
            <Paper withBorder p="xs">
              <Group spacing="xs" align="center" style={{ marginBottom: 8 }}>
                <Text size="sm" weight={500}>Doc Status:</Text>
                <Badge>Open</Badge>
              </Group>
              <Field label="Hold by/on" placeholder="—" />
            </Paper>
          </Grid.Col>

          <Grid.Col span={4}>
            <Paper withBorder p="xs">
              <Text size="sm" weight={500} mb="xs">Customer Info</Text>
              <Text size="xs" color="dimmed">
                {customer ? customer.customerName : '10 TANKER AIR CARRIER'}
              </Text>
            </Paper>
          </Grid.Col>

          {/* Customer + FOB section */}
          <Grid.Col span={4}>
            <Paper withBorder p="xs">
              <Field 
                label="Customer Code" 
                defaultValue={customer ? customer.customerNumber : "10 TANKER"} 
                readOnly 
              />
              <Field 
                label="Customer Name" 
                defaultValue={customer ? customer.customerName : "10 TANKER AIR CARRIER"} 
                readOnly 
              />
              <Field label="Attn" placeholder="—" />
              <Field label="F.O.B." placeholder="—" showEllipsis onEllipsisClick={() => handleEllipsisClick('F.O.B.')} />
              <Field label="Org" placeholder="—" showEllipsis onEllipsisClick={() => handleEllipsisClick('Organization')} />
              <Field label="Ship Via" placeholder="—" />
              <Field 
                label="Terms" 
                component={Select} 
                data={["NET 30", "COD", "PREPAID"]} 
                defaultValue={customer ? customer.paymentTerms : "NET 30"} 
              />
              <Field label="Prepared by" defaultValue="ZKW" showEllipsis onEllipsisClick={() => handleEllipsisClick('Prepared by')} />
              <Field label="Priority" placeholder="—" />
            </Paper>
          </Grid.Col>

          <Grid.Col span={4}>
            <Paper withBorder p="xs">
              <Field label="Entered" defaultValue="7/30/2025" readOnly />
              <DateField label="Req On" value={defaultDate} />
              <DateField label="Due On" value={defaultDate} />
              <DateField label="Priced" value={pricedDate} />
              <DateField label="Quoted" value={quotedDate} />
              <Field label="Valid For" defaultValue="30" />
              <DateField label="Valid Until" value={validUntilDate} />
              <Field label="Last Print" placeholder="—" />
              <Field label="Revision" placeholder="—" />
            </Paper>
          </Grid.Col>

          <Grid.Col span={4}>
            <Paper withBorder p="xs">
              <Field label="Sub Total" defaultValue="0.00" readOnly />
              <Field label="Discount" defaultValue="0.00" readOnly />
              <Field label="Misc Ttl" defaultValue="0.00" readOnly />
              <Field label="Tax Ttl" defaultValue="0.00" readOnly />
              <Field label="Doc Total" defaultValue="0.00" readOnly />
              <Field label="Crcy Base" defaultValue="USD" readOnly />
              <Field label="Crcy Conv" defaultValue="USD" readOnly />
            </Paper>
          </Grid.Col>
        </Grid>
      </Collapse>

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalTitle}
        size="md"
        centered
      >
        <Text>This is a placeholder search dialog for {modalTitle}.</Text>
        <Group position="right" mt="md">
          <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
          <Button onClick={() => setModalOpen(false)}>Select</Button>
        </Group>
      </Modal>
    </Box>
  );
}